import React, { useMemo } from 'react';
import { FireOutlined } from '@ant-design/icons';

const BUCKET_COUNT = 8;

function calcBuckets(filteredPrices, myPrice) {
    if (!filteredPrices || filteredPrices.length === 0) return [];

    const min = filteredPrices[0];
    const max = filteredPrices[filteredPrices.length - 1];
    const range = max - min;
    if (range === 0)
        return [
            {
                label: `${(min / 10000).toFixed(1)}만`,
                min,
                max,
                count: filteredPrices.length,
                hasMe: true,
            },
        ];

    const step = range / BUCKET_COUNT;

    const buckets = Array.from({ length: BUCKET_COUNT }, (_, i) => {
        const lo = min + step * i;
        const hi = i === BUCKET_COUNT - 1 ? max + 1 : min + step * (i + 1);
        const count = filteredPrices.filter(p => p >= lo && p < hi).length;
        const hasMe = myPrice >= lo && myPrice < hi;
        const fmt = v => {
            if (v >= 10000) return `${(v / 10000).toFixed(v % 10000 === 0 ? 0 : 1)}만`;
            return `${(v / 1000).toFixed(0)}k`;
        };
        return { label: `${fmt(lo)}~${fmt(hi)}`, lo, hi, count, hasMe };
    });

    return buckets;
}

function priceAdvice(myPrice, avgPrice, _filteredPrices) {
    if (!avgPrice) return null;
    const diff = myPrice - avgPrice;
    const pct = Math.round((Math.abs(diff) / avgPrice) * 100);

    if (Math.abs(diff) < avgPrice * 0.05) {
        return {
            label: '적정 가격 — 중간 포지션',
            color: '#10b981',
            text: `평균가(${avgPrice.toLocaleString()}원)와 비슷한 가격대입니다. 리뷰, 이미지 품질 등으로 차별화하세요.`,
        };
    }
    if (diff < 0) {
        return {
            label: `저가 포지션 — 평균 대비 ${pct}% 낮음`,
            color: '#3b82f6',
            text: `가격 경쟁력이 있습니다. 품질 어필을 강화하면 전환율을 높일 수 있습니다.`,
        };
    }
    return {
        label: `고가 포지션 — 평균 대비 ${pct}% 높음`,
        color: '#f59e0b',
        text: `프리미엄 포지셔닝 전략입니다. 차별화된 품질·브랜드 가치 소구가 필요합니다.`,
    };
}

const PricePositioningSection = ({ report }) => {
    const pm = report?.priceMap;

    const buckets = useMemo(() => {
        if (!pm) return [];
        return calcBuckets(pm.filteredPrices, pm.myPrice);
    }, [pm]);

    const advice = useMemo(() => {
        if (!pm) return null;
        return priceAdvice(pm.myPrice, pm.avgPrice, pm.filteredPrices);
    }, [pm]);

    const maxCount = buckets.length ? Math.max(...buckets.map(b => b.count), 1) : 1;

    if (!pm) return null;

    return (
        <>
            <div className="section-title-premium">
                <FireOutlined style={{ color: '#f59e0b' }} /> 가격 포지셔닝 맵
                <span
                    style={{ fontSize: 11, color: '#9ca3af', fontWeight: 400, marginLeft: 'auto' }}
                >
                    상위 {pm.totalCount}개 상품 기준
                    {pm.outlierCount > 0 ? ` (이상치 ${pm.outlierCount}개 제외)` : ''}
                </span>
            </div>

            <div className="diag-card" style={{ padding: 28 }}>
                {/* 상단 지표 3개 */}
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr 1fr',
                        gap: 1,
                        marginBottom: 28,
                        background: '#e2e8f0',
                        borderRadius: 12,
                        overflow: 'hidden',
                    }}
                >
                    {[
                        {
                            label: '내 가격',
                            value: `${pm.myPrice.toLocaleString()}원`,
                            color: '#6366f1',
                            bold: true,
                        },
                        {
                            label: '평균가',
                            value: `${pm.avgPrice.toLocaleString()}원`,
                            color: '#0f172a',
                        },
                        {
                            label: '가격 범위',
                            value: `${pm.filteredPrices[0].toLocaleString()}원 ~ ${pm.filteredPrices[pm.filteredPrices.length - 1].toLocaleString()}원`,
                            color: '#0f172a',
                        },
                    ].map(s => (
                        <div
                            key={s.label}
                            style={{
                                background: '#fff',
                                padding: '16px 24px',
                                textAlign: 'center',
                            }}
                        >
                            <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 4 }}>
                                {s.label}
                            </div>
                            <div style={{ fontSize: 18, fontWeight: 800, color: s.color }}>
                                {s.value}
                            </div>
                        </div>
                    ))}
                </div>

                {/* 가격 구간 바 차트 */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {buckets.map(b => {
                        const pct = Math.round((b.count / maxCount) * 100);
                        return (
                            <div
                                key={b.label}
                                style={{ display: 'flex', alignItems: 'center', gap: 12 }}
                            >
                                <div
                                    style={{
                                        width: 80,
                                        fontSize: 12,
                                        color: '#64748b',
                                        textAlign: 'right',
                                        flexShrink: 0,
                                    }}
                                >
                                    {b.label}
                                </div>
                                <div
                                    style={{
                                        flex: 1,
                                        background: '#f1f5f9',
                                        borderRadius: 6,
                                        height: 28,
                                        position: 'relative',
                                        overflow: 'hidden',
                                    }}
                                >
                                    <div
                                        style={{
                                            width: `${pct}%`,
                                            height: '100%',
                                            background: b.hasMe
                                                ? 'linear-gradient(90deg, #6366f1, #818cf8)'
                                                : '#cbd5e1',
                                            borderRadius: 6,
                                            transition: 'width 0.4s ease',
                                            display: 'flex',
                                            alignItems: 'center',
                                            paddingLeft: 10,
                                        }}
                                    >
                                        {b.hasMe && (
                                            <span
                                                style={{
                                                    fontSize: 11,
                                                    fontWeight: 800,
                                                    color: '#fff',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                MY
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div
                                    style={{
                                        width: 24,
                                        fontSize: 13,
                                        fontWeight: b.hasMe ? 900 : 600,
                                        color: b.hasMe ? '#6366f1' : '#64748b',
                                        textAlign: 'right',
                                        flexShrink: 0,
                                    }}
                                >
                                    {b.count}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* 포지션 안내 */}
                {advice && (
                    <div
                        style={{
                            marginTop: 20,
                            borderLeft: `3px solid ${advice.color}`,
                            paddingLeft: 14,
                            background: '#f8fafc',
                            borderRadius: '0 8px 8px 0',
                            padding: '12px 14px 12px 16px',
                        }}
                    >
                        <div
                            style={{
                                fontSize: 13,
                                fontWeight: 800,
                                color: advice.color,
                                marginBottom: 4,
                            }}
                        >
                            {advice.label}
                        </div>
                        <div style={{ fontSize: 12, color: '#64748b' }}>{advice.text}</div>
                    </div>
                )}
            </div>
        </>
    );
};

export default PricePositioningSection;
